import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Switch } from "@/components/ui/switch";

const KEY = "pm_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState({ stats: false, marketing: false });

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setVisible(true);
    const open = () => {
      setVisible(true);
      setCustomizing(true);
    };
    window.addEventListener("pm:open-cookie-prefs", open);
    return () => window.removeEventListener("pm:open-cookie-prefs", open);
  }, []);

  const save = (p) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({ necessary: true, ...p, date: new Date().toISOString() })
    );
    setVisible(false);
    setCustomizing(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-4 bottom-4 z-[60] rounded-lg border border-white/10 bg-coal/95 p-6 shadow-2xl backdrop-blur-xl sm:bottom-6 sm:left-1/2 sm:right-auto sm:w-full sm:max-w-md sm:-translate-x-1/2"
        >
          <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-ivory">Cookies</h2>
          <p className="mt-3 text-sm leading-relaxed text-ivory/55">
            Nous utilisons des cookies pour assurer le bon fonctionnement du site et, avec votre
            accord, mesurer son audience. Vous pouvez accepter, refuser ou personnaliser vos choix.
          </p>

          {customizing && (
            <div className="mt-5 space-y-4 border-t border-white/10 pt-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-ivory">Cookies nécessaires</p>
                  <p className="text-xs text-ivory/40">Indispensables au fonctionnement du site.</p>
                </div>
                <Switch checked disabled data-testid="cookie-necessary-switch" />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-ivory">Statistiques</p>
                  <p className="text-xs text-ivory/40">Mesure d'audience anonymisée.</p>
                </div>
                <Switch
                  checked={prefs.stats}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, stats: v }))}
                  data-testid="cookie-stats-switch"
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-ivory">Marketing</p>
                  <p className="text-xs text-ivory/40">Contenus et réseaux sociaux tiers.</p>
                </div>
                <Switch
                  checked={prefs.marketing}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  data-testid="cookie-marketing-switch"
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {customizing ? (
              <button
                data-testid="cookie-save-button"
                onClick={() => save(prefs)}
                className="btn-primary !h-10 !px-6 !text-[11px]"
              >
                Enregistrer mes choix
              </button>
            ) : (
              <>
                <button
                  data-testid="cookie-accept-button"
                  onClick={() => save({ stats: true, marketing: true })}
                  className="btn-primary !h-10 !px-6 !text-[11px]"
                >
                  Accepter
                </button>
                <button
                  data-testid="cookie-refuse-button"
                  onClick={() => save({ stats: false, marketing: false })}
                  className="btn-outline !h-10 !px-6 !text-[11px]"
                >
                  Refuser
                </button>
                <button
                  data-testid="cookie-customize-button"
                  onClick={() => setCustomizing(true)}
                  className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-copper"
                >
                  Personnaliser
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
